import { ref, computed, watch, onBeforeUnmount } from 'vue';
import ApiClient from '@/ApiClient';
import {
  RECOGNITION_REDEMPTION_METHOD_CHOICES,
  RECOGNITION_BUDGET_CADENCE_OPTIONS,
  DAYS_IN_MONTH,
  toMonthNumber,
  toTwoDecimalsOrEmpty,
} from '@/pages/recognition/constants';
import State from '@/state/State';
import { currencySymbolFromCode } from '@/utils/common';

/**
 * Shared composable for award creation forms.
 * Encapsulates common state, computed properties, and helper functions
 * used across Milestone, Open, and Program award types.
 */
export function useAwardForm() {
  // ─────────────────────────────────────────────────────────────────────────────
  // Basic Details
  // ─────────────────────────────────────────────────────────────────────────────
  const awardForm = ref({ name: '' });
  const divisions = ref([]);
  const selectedDivisionIds = ref([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Monetary Reward
  // ─────────────────────────────────────────────────────────────────────────────
  const monetaryRewardEnabled = ref(false);
  const awardValueMinor = ref(undefined); // number | number[] | undefined
  const awardValueError = ref('');
  const defaultAwardValueMinor = ref(undefined); // number | undefined
  const selectedRedemptionOptions = ref([RECOGNITION_REDEMPTION_METHOD_CHOICES[0].value]);
  const redemptionSelectionError = ref('');
  const expirationMonths = ref('');

  // ─────────────────────────────────────────────────────────────────────────────
  // Budget Settings
  // ─────────────────────────────────────────────────────────────────────────────
  const rewardBudgetAmount = ref('');
  const rewardBudgetCadence = ref(undefined);
  const budgetYearStartMonth = ref(undefined);
  const budgetYearStartDay = ref(undefined);

  // ─────────────────────────────────────────────────────────────────────────────
  // Image Upload
  // ─────────────────────────────────────────────────────────────────────────────
  const bannerFile = ref(null);
  const bannerPreviewUrl = ref('');
  const bannerS3Key = ref('');
  const bannerRemoved = ref(false);
  const badgeFile = ref(null);
  const badgePreviewUrl = ref('');
  const badgeS3Key = ref('');
  const badgeRemoved = ref(false);
  const allowedImageTypes = ['image/jpeg', 'image/png'];
  const MAX_BANNER_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

  // ─────────────────────────────────────────────────────────────────────────────
  // UI State
  // ─────────────────────────────────────────────────────────────────────────────
  const saving = ref(false);
  const errorMessage = ref('');

  // ─────────────────────────────────────────────────────────────────────────────
  // Currency
  // ─────────────────────────────────────────────────────────────────────────────
  const currencySymbol = computed(() => currencySymbolFromCode(State.state?.company?.default_currency));

  // ─────────────────────────────────────────────────────────────────────────────
  // Computed: Divisions
  // ─────────────────────────────────────────────────────────────────────────────
  const divisionOptions = computed(() => divisions.value.map(d => ({ text: d.name, value: d.division_id })));

  const allDivisionIds = computed(() => (divisions.value || []).map(d => d.division_id));

  const isAllDivisionsSelected = computed(() => {
    const selected = selectedDivisionIds.value || [];
    const all = allDivisionIds.value || [];
    return (
      selected.length > 0 && all.length > 0 && selected.length === all.length && all.every(id => selected.includes(id))
    );
  });

  const enabledDivisions = computed(() => {
    const selected = selectedDivisionIds.value || [];
    // Backend contract: empty array means "entire company" (all employees).
    // Only treat an empty selection as "entire company".
    // When specific divisions are selected (even if all of them), send those IDs.
    return selected.length === 0 ? [] : selected.map(String);
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Computed: Budget Year Start
  // ─────────────────────────────────────────────────────────────────────────────
  const budgetYearStartFromCompany = computed(() => {
    const props = State?.state?.company?.properties || {};
    const recognitionProps = props.recognition_properties || {};

    const month = recognitionProps.budget_period_financial_year_start_month ?? toMonthNumber(props.budget_year_start);
    const day = recognitionProps.budget_period_financial_year_start_day ?? undefined;

    return { month, day };
  });

  const budgetYearStartDayOptions = computed(() => {
    const month = Number(budgetYearStartMonth.value);
    if (!month || !DAYS_IN_MONTH[month]) {
      return [];
    }
    const max = DAYS_IN_MONTH[month];
    return Array.from({ length: max }, (_, i) => {
      const day = i + 1;
      return { text: String(day), value: day };
    });
  });

  const budgetYearStartDisabled = computed(
    () =>
      budgetYearStartFromCompany.value.month !== undefined &&
      budgetYearStartFromCompany.value.month !== null &&
      String(budgetYearStartFromCompany.value.month) !== '',
  );

  const shouldRequireBudgetYearStart = computed(() => {
    if (!monetaryRewardEnabled.value || budgetYearStartDisabled.value) {
      return false;
    }
    const hasBudgetAmount = String(rewardBudgetAmount.value || '').trim() !== '';
    const cadence = rewardBudgetCadence.value;
    const quarterly = RECOGNITION_BUDGET_CADENCE_OPTIONS[0].value;
    const yearly = RECOGNITION_BUDGET_CADENCE_OPTIONS[1].value;
    return hasBudgetAmount && (cadence === quarterly || cadence === yearly);
  });

  const budgetYearStartMonthRules = computed(() => (shouldRequireBudgetYearStart.value ? 'required' : ''));
  const budgetYearStartDayRules = computed(() =>
    shouldRequireBudgetYearStart.value && budgetYearStartMonth.value ? 'required' : '',
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Helper Functions
  // ─────────────────────────────────────────────────────────────────────────────
  const sanitizeAmountInput = raw => {
    const cleaned = String(raw ?? '').replace(/[^0-9.]/g, '');
    const [intPart, decPartRaw] = cleaned.split('.');
    if (decPartRaw !== undefined) {
      return `${intPart || '0'}.${decPartRaw.slice(0, 2)}`;
    }
    return intPart || '';
  };

  const majorToMinor = major => {
    const n = parseFloat(String(major).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(n)) {
      return undefined;
    }
    return Math.round(n * 100);
  };

  const onAmountKeydown = e => {
    const isDigit = /^[0-9]$/.test(e.key);
    const isDot = e.key === '.';
    const isControlKey =
      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key) ||
      e.ctrlKey ||
      e.metaKey;
    if (isControlKey) {
      return;
    }

    const el = e.target;
    const value = String(el?.value ?? '');
    const selectionStart = el?.selectionStart ?? value.length;
    const selectionEnd = el?.selectionEnd ?? value.length;

    if (isDot && value.includes('.')) {
      e.preventDefault();
      return;
    }
    if (isDigit) {
      const before = value.slice(0, selectionStart);
      const after = value.slice(selectionEnd);
      const next = `${before}${e.key}${after}`;
      const match = next.match(/^\d*(?:\.(\d*))?/);
      const dec = match && match[1] ? match[1] : '';
      if (dec.length > 2) {
        e.preventDefault();
        return;
      }
    }
    if (!isDigit && !isDot) {
      e.preventDefault();
    }
  };

  const onExpirationInput = value => {
    const cleaned = String(value ?? '').replace(/[^0-9]/g, '');
    expirationMonths.value = cleaned;
  };

  const onRewardBudgetAmountInput = value => {
    const raw = String(value ?? '').replace(/[^0-9.]/g, '');
    const [intPart, decPartRaw] = raw.split('.');
    if (decPartRaw !== undefined) {
      rewardBudgetAmount.value = `${intPart || '0'}.${decPartRaw.slice(0, 2)}`;
    } else {
      rewardBudgetAmount.value = intPart || '';
    }
  };

  const onRewardBudgetBlur = () => {
    rewardBudgetAmount.value = rewardBudgetAmount.value === '' ? '' : toTwoDecimalsOrEmpty(rewardBudgetAmount.value);
  };

  const onDefaultAwardValueMinorChange = val => {
    defaultAwardValueMinor.value = val;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Image Upload Handlers
  // ─────────────────────────────────────────────────────────────────────────────
  const revokeObjectUrl = url => {
    if (url) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    }
  };

  const onBannerFileUpdate = val => {
    bannerFile.value = val;
    if (val) {
      bannerS3Key.value = '';
      bannerRemoved.value = false;
    }
  };

  const onBannerPreviewUrlUpdate = val => {
    // Revoke previous URL before storing the new one
    if (bannerPreviewUrl.value && bannerPreviewUrl.value !== val) {
      revokeObjectUrl(bannerPreviewUrl.value);
    }
    bannerPreviewUrl.value = val;
  };

  const onBadgeFileUpdate = val => {
    badgeFile.value = val;
    if (val) {
      badgeS3Key.value = '';
      badgeRemoved.value = false;
    }
  };

  const onBadgePreviewUrlUpdate = val => {
    // Revoke previous URL before storing the new one
    if (badgePreviewUrl.value && badgePreviewUrl.value !== val) {
      revokeObjectUrl(badgePreviewUrl.value);
    }
    badgePreviewUrl.value = val;
  };

  const onBannerGallerySelect = ({ url, s3Key }) => {
    bannerS3Key.value = s3Key;
    bannerPreviewUrl.value = url;
    bannerFile.value = null;
    bannerRemoved.value = false;
  };

  const onBadgeGallerySelect = ({ url, s3Key }) => {
    badgeS3Key.value = s3Key;
    badgePreviewUrl.value = url;
    badgeFile.value = null;
    badgeRemoved.value = false;
  };

  const onBannerRemove = () => {
    revokeObjectUrl(bannerPreviewUrl.value);
    bannerFile.value = null;
    bannerPreviewUrl.value = '';
    bannerS3Key.value = '';
    bannerRemoved.value = true;
  };

  const onBadgeRemove = () => {
    revokeObjectUrl(badgePreviewUrl.value);
    badgeFile.value = null;
    badgePreviewUrl.value = '';
    badgeS3Key.value = '';
    badgeRemoved.value = true;
  };

  // Clean up object URLs when the component unmounts to prevent memory leaks
  onBeforeUnmount(() => {
    revokeObjectUrl(bannerPreviewUrl.value);
    revokeObjectUrl(badgePreviewUrl.value);
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Data Loading
  // ─────────────────────────────────────────────────────────────────────────────
  const loadDivisions = async () => {
    try {
      divisions.value = (await ApiClient.getDivisions())?.data || [];
    } catch {
      // silently ignore for now
    }
  };

  const initBudgetYearStart = () => {
    budgetYearStartMonth.value = budgetYearStartFromCompany.value.month;
    budgetYearStartDay.value = budgetYearStartFromCompany.value.day;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Watchers
  // ─────────────────────────────────────────────────────────────────────────────
  const setupCommonWatchers = () => {
    watch(selectedRedemptionOptions, newVal => {
      if (redemptionSelectionError.value && Array.isArray(newVal) && newVal.length > 0) {
        redemptionSelectionError.value = '';
      }
    });

    watch(awardValueMinor, newVal => {
      if (!monetaryRewardEnabled.value) {
        awardValueError.value = '';
        return;
      }
      const values = Array.isArray(newVal)
        ? newVal.filter(v => Number.isFinite(v))
        : Number.isFinite(newVal)
        ? [newVal]
        : [];
      if (values.length > 0 && awardValueError.value) {
        awardValueError.value = '';
      }
    });
  };

  const resetMonetaryRewardFields = () => {
    selectedRedemptionOptions.value = [RECOGNITION_REDEMPTION_METHOD_CHOICES[0].value];
    redemptionSelectionError.value = '';
    awardValueMinor.value = undefined;
    awardValueError.value = '';
    rewardBudgetAmount.value = '';
    rewardBudgetCadence.value = undefined;
    budgetYearStartMonth.value = budgetYearStartFromCompany.value.month;
    budgetYearStartDay.value = budgetYearStartFromCompany.value.day;
    expirationMonths.value = '';
    defaultAwardValueMinor.value = undefined;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Payload Building Helpers
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Converts expiration months to ISO 8601 duration format for the API.
   * The API expects grant_expiry_interval as a top-level field in ISO 8601 format.
   * We use days (e.g., "P360D" for 12 months) instead of months ("P12M") because
   * the isodate library returns a Duration object for month-based durations which
   * isn't JSON serializable, whereas day-based durations return a timedelta.
   * @returns {string|undefined} ISO 8601 duration string or undefined if not set
   */
  const getGrantExpiryInterval = () => {
    if (!monetaryRewardEnabled.value) {
      return undefined;
    }
    const monthsInt = parseInt(String(expirationMonths.value || '').trim(), 10);
    if (Number.isFinite(monthsInt) && monthsInt > 0) {
      // Convert months to days (30 days per month)
      const days = monthsInt * 30;
      return `P${days}D`;
    }
    return undefined;
  };

  const buildBaseProperties = (redemptionMethods = []) => {
    // API requires at least 1 valid_grant_type - default to VOUCHER if none provided
    const grantTypes = (redemptionMethods || []).map(String);
    const props = {
      valid_grant_types: grantTypes.length > 0 ? grantTypes : ['VOUCHER'],
    };

    // When monetary rewards are disabled, explicitly set fields to null to clear them
    if (!monetaryRewardEnabled.value) {
      props.award_value_minor = null;
      props.default_award_value_minor = null;
      props.budget_period = null;
      return props;
    }

    const hasBudgetAmount = String(rewardBudgetAmount.value || '').trim() !== '';
    if (rewardBudgetCadence.value && hasBudgetAmount) {
      props.budget_period = String(rewardBudgetCadence.value);
    }

    if (awardValueMinor.value !== undefined && awardValueMinor.value !== null) {
      const val = awardValueMinor.value;
      if (Array.isArray(val)) {
        const cleaned = val.filter(v => Number.isFinite(v));
        if (cleaned.length === 1) {
          props.award_value_minor = cleaned[0];
        } else if (cleaned.length > 1) {
          props.award_value_minor = cleaned;
        }
      } else if (Number.isFinite(val)) {
        props.award_value_minor = val;
      }
    }

    if (
      defaultAwardValueMinor.value !== undefined &&
      defaultAwardValueMinor.value !== null &&
      Number.isFinite(defaultAwardValueMinor.value)
    ) {
      props.default_award_value_minor = defaultAwardValueMinor.value;
    }

    return props;
  };

  const getImageData = () => {
    const files = {};
    const s3Keys = {};
    if (bannerFile.value) {
      files.banner = bannerFile.value;
    } else if (bannerS3Key.value) {
      s3Keys.banner_s3_key = bannerS3Key.value;
    } else if (bannerRemoved.value) {
      s3Keys.banner_s3_key = null;
    }
    if (badgeFile.value) {
      files.badge = badgeFile.value;
    } else if (badgeS3Key.value) {
      s3Keys.badge_s3_key = badgeS3Key.value;
    } else if (badgeRemoved.value) {
      s3Keys.badge_s3_key = null;
    }
    return { files, s3Keys };
  };

  const getBudgetMinor = () => {
    // When monetary rewards are disabled, explicitly return null to clear the budget
    if (!monetaryRewardEnabled.value) {
      return null;
    }
    const amountStr = String(rewardBudgetAmount.value || '').trim();
    if (amountStr === '') {
      return undefined;
    }
    const amount = parseFloat(amountStr.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(amount) || amount < 0) {
      return undefined;
    }
    // Convert major units (pounds/dollars) to minor units (pence/cents)
    return Math.round(amount * 100);
  };

  const persistBudgetYearStart = async () => {
    if (!shouldRequireBudgetYearStart.value || budgetYearStartDisabled.value) {
      return { success: true };
    }

    const companyId = State?.state?.company?.company_id;
    if (!companyId) {
      return { success: true };
    }

    const recognitionProps =
      (State?.state?.company?.properties?.recognition_properties && {
        ...State.state.company.properties.recognition_properties,
      }) ||
      {};

    const month = budgetYearStartMonth.value ? Number(budgetYearStartMonth.value) : undefined;
    const day = budgetYearStartDay.value ? Number(budgetYearStartDay.value) : undefined;

    if (!month || !day) {
      return {
        success: false,
        error: !month ? 'Please select your budget year start month.' : 'Please select your budget year start day.',
      };
    }

    await ApiClient.smeUpdateCompanyProperties(companyId, {
      properties: {
        recognition_properties: {
          ...recognitionProps,
          budget_period_financial_year_start_month: month,
          budget_period_financial_year_start_day: day,
        },
      },
    });

    try {
      await ApiClient.getUser(); // refresh local state
    } catch {
      // non-fatal
    }

    return { success: true };
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Validation Helpers
  // ─────────────────────────────────────────────────────────────────────────────
  const validateRedemptionOptions = redemptionMethods => {
    if (monetaryRewardEnabled.value && !redemptionMethods.length) {
      redemptionSelectionError.value = 'Please select at least one redemption method.';
      return false;
    }
    redemptionSelectionError.value = '';
    return true;
  };

  const validateAwardValues = () => {
    if (!monetaryRewardEnabled.value) {
      return true;
    }
    const val = awardValueMinor.value;
    const values = Array.isArray(val)
      ? val.filter(v => Number.isFinite(v) && v > 0)
      : Number.isFinite(val) && val > 0
      ? [val]
      : [];
    if (values.length === 0) {
      awardValueError.value = 'Please add at least one reward amount.';
      return false;
    }
    awardValueError.value = '';
    return true;
  };

  const validateBudgetCadence = () => {
    if (
      monetaryRewardEnabled.value &&
      String(rewardBudgetAmount.value || '').trim() !== '' &&
      !rewardBudgetCadence.value
    ) {
      errorMessage.value = 'Please select a reward budget cadence.';
      return false;
    }
    return true;
  };

  return {
    // Currency
    currencySymbol,

    // Basic Details
    awardForm,
    divisions,
    selectedDivisionIds,
    divisionOptions,
    allDivisionIds,
    isAllDivisionsSelected,
    enabledDivisions,

    // Monetary Reward
    monetaryRewardEnabled,
    awardValueMinor,
    awardValueError,
    defaultAwardValueMinor,
    selectedRedemptionOptions,
    redemptionSelectionError,
    expirationMonths,

    // Budget Settings
    rewardBudgetAmount,
    rewardBudgetCadence,
    budgetYearStartMonth,
    budgetYearStartDay,
    budgetYearStartFromCompany,
    budgetYearStartDayOptions,
    budgetYearStartDisabled,
    shouldRequireBudgetYearStart,
    budgetYearStartMonthRules,
    budgetYearStartDayRules,

    // Image Upload
    bannerFile,
    bannerPreviewUrl,
    bannerS3Key,
    badgeFile,
    badgePreviewUrl,
    badgeS3Key,
    allowedImageTypes,
    MAX_BANNER_SIZE_BYTES,
    onBannerFileUpdate,
    onBannerPreviewUrlUpdate,
    onBadgeFileUpdate,
    onBadgePreviewUrlUpdate,
    onBannerGallerySelect,
    onBadgeGallerySelect,
    onBannerRemove,
    onBadgeRemove,

    // UI State
    saving,
    errorMessage,

    // Helper Functions
    sanitizeAmountInput,
    majorToMinor,
    onAmountKeydown,
    onExpirationInput,
    onRewardBudgetAmountInput,
    onRewardBudgetBlur,
    onDefaultAwardValueMinorChange,

    // Data Loading
    loadDivisions,
    initBudgetYearStart,

    // Watchers
    setupCommonWatchers,
    resetMonetaryRewardFields,

    // Payload Building
    buildBaseProperties,
    getImageData,
    getBudgetMinor,
    getGrantExpiryInterval,
    persistBudgetYearStart,

    // Validation
    validateRedemptionOptions,
    validateAwardValues,
    validateBudgetCadence,

    // Constants (re-export for convenience)
    RECOGNITION_REDEMPTION_METHOD_CHOICES,
    RECOGNITION_BUDGET_CADENCE_OPTIONS,
  };
}
